import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Country, DonationField, RegularExpressions } from '../../constants';
import { DonationFormLabel } from '../donationFormLabel';
import { SelectCountry } from '../selectField';

import { getError } from '../../utils';
import { FormErrors } from '../../types';

export const YourInfo: FC = (): JSX.Element => {
  const [country, setCountry] = useState(Country.UnitedStates);
  const [optIn, setOptIn] = useState(false);
  const isUSCan =
    country === Country.UnitedStates || country === Country.Canada;
  const { control, register, formState, setValue, clearErrors } =
    useFormContext();

  const { errors }: { errors: FormErrors } = formState;

  useEffect(() => {
    setOptIn(country === Country.UnitedStates);
    setValue(DonationField.country, country);
  }, [country, setValue]);

  return (
    <VStack spacing={10} w="full">
      <VStack align="left" spacing={6} w="full">
        <Text as="h3" fontSize="1.5rem">
          Your Information
        </Text>

        <Controller
          control={control}
          name={DonationField.country}
          render={({ field, fieldState }) => (
            <FormControl isInvalid={!!fieldState.error} isRequired>
              <DonationFormLabel htmlFor={DonationField.country}>
                Country
              </DonationFormLabel>
              <SelectCountry
                {...field}
                aria-label={DonationField.country}
                errors={errors}
                id={DonationField.country}
                onChange={({
                  currentTarget: { value },
                }: ChangeEvent<HTMLSelectElement>) => {
                  setCountry(value as Country);
                  field.onChange(value);
                  clearErrors(DonationField.postCode);
                }}
              />
              <FormErrorMessage>{fieldState?.error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <FormControl
          isInvalid={!!getError(DonationField.firstName, errors)}
          isRequired
        >
          <DonationFormLabel htmlFor={DonationField.firstName}>
            First Name
          </DonationFormLabel>
          <Input
            maxLength={50}
            id={DonationField.firstName}
            pattern={RegularExpressions.isValidNameOrLastName}
            {...register(DonationField.firstName)}
            data-testid="first-name-input"
            onBlurCapture={({
              currentTarget,
            }: ChangeEvent<HTMLInputElement>) => {
              const trimmedValue = currentTarget.value.trim();
              setValue(DonationField.firstName, trimmedValue);
            }}
          />
          <FormErrorMessage>
            {getError(DonationField.firstName, errors)}
          </FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={!!getError(DonationField.lastName, errors)}
          isRequired
        >
          <DonationFormLabel htmlFor={DonationField.lastName}>
            Last Name
          </DonationFormLabel>
          <Input
            id={DonationField.lastName}
            maxLength={50}
            pattern={RegularExpressions.isValidNameOrLastName}
            type="text"
            {...register(DonationField.lastName)}
            data-testid="last-name-input"
            onBlurCapture={({
              currentTarget,
            }: ChangeEvent<HTMLInputElement>) => {
              const trimmedValue = currentTarget.value.trim();
              setValue(DonationField.lastName, trimmedValue);
            }}
          />
          <FormErrorMessage>
            {getError(DonationField.lastName, errors)}
          </FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={!!getError(DonationField.donorEmail, errors)}
          isRequired
        >
          <DonationFormLabel htmlFor={DonationField.donorEmail}>
            Email Address
          </DonationFormLabel>
          <FormHelperText>
            We'll send your receipt to this address
          </FormHelperText>
          <Input
            id={DonationField.donorEmail}
            maxLength={100}
            {...register(DonationField.donorEmail)}
            data-testid="email-input"
            onBlurCapture={({
              currentTarget,
            }: ChangeEvent<HTMLInputElement>) => {
              const trimmedValue = currentTarget.value.trim();
              setValue(DonationField.donorEmail, trimmedValue);
              // setEmail(trimmedValue);
            }}
          />
          <FormErrorMessage>
            {getError(DonationField.donorEmail, errors)}
          </FormErrorMessage>
        </FormControl>

        {isUSCan && (
          <FormControl>
            <Checkbox
              checked={optIn}
              {...register(DonationField.donorEmailOptIn)}
              onChange={() => setOptIn(!optIn)}
            >
              I would like to receive emails from St. Jude
            </Checkbox>
          </FormControl>
        )}
      </VStack>
    </VStack>
  );
};
